// src/pages/MyQueuePage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { getUser } from '../utils/auth';
import { fmtDate, timeAgo } from '../utils/formatters';
import { RefreshCw, Search, Clock, AlertCircle } from 'lucide-react';

const BORDER = 'var(--white-07)';
const PRIMARY_TEXT = 'var(--text-primary)';
const SECONDARY_TEXT = 'var(--text-muted)';

const STATUS_STYLES = {
  'Open': { bg: 'rgba(96, 165, 250, 0.12)', color: 'var(--text-60a5fa)', border: 'rgba(96, 165, 250, 0.25)' },
  'In Progress': { bg: 'rgba(96, 165, 250, 0.12)', color: 'var(--text-60a5fa)', border: 'rgba(96, 165, 250, 0.25)' },
  'On Hold': { bg: 'rgba(245, 158, 11, 0.12)', color: '#F59E0B', border: 'rgba(245, 158, 11, 0.25)' },
  'Resolved': { bg: 'rgba(16, 185, 129, 0.12)', color: 'var(--text-34d399)', border: 'rgba(16, 185, 129, 0.25)' },
  'Closed': { bg: 'rgba(107, 114, 128, 0.1)', color: '#9CA3AF', border: 'rgba(107, 114, 128, 0.2)' },
};

const PRIORITY_COLORS = { critical: '#F87171', high: '#FB923C', medium: 'var(--text-60a5fa)', low: '#6B7280' };

export default function MyQueuePage({ showToast }) {
  const [tickets, setTickets] = useState([]);
  const [search, setSearch] = useState('');
  const [hoveredRow, setHoveredRow] = useState(null);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedSort, setSelectedSort] = useState('Latest Activity');
  const [showSort, setShowSort] = useState(false);
  const { request, loading } = useApi();
  const navigate = useNavigate();
  const currentUser = getUser();

  const loadQueue = useCallback(async () => {
    const trimmedSearch = search.trim();
    if (trimmedSearch && !trimmedSearch.match(/^BST-INT\d+$/)) {
      return;
    }
    try {
      const data = await request(`/api/tickets?filter=assignedtome`);
      setTickets(data);
    } catch (err) {
      showToast(err.message || 'Failed to load queue', 'error');
    }
  }, [request, showToast, search]);

  useEffect(() => { loadQueue(); }, [loadQueue]);

  const filteredTickets = tickets.filter(t => {
    const trimmedSearch = search.trim();
    if (!trimmedSearch) return true;
    const match = trimmedSearch.match(/^BST-INT(\d+)$/);
    if (!match) {
      // Also allow searching by title if it's not a direct ID match
      return t.title.toLowerCase().includes(trimmedSearch.toLowerCase()) ||
             (t.category || '').toLowerCase().includes(trimmedSearch.toLowerCase());
    }
    const ticketId = parseInt(match[1], 10);
    return t.id === ticketId;
  });

  const sortedTickets = [...filteredTickets].sort((a, b) => {
    if (selectedSort === 'Latest Activity') return new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at);
    if (selectedSort === 'Oldest Activity') return new Date(a.updated_at || a.created_at) - new Date(b.updated_at || b.created_at);
    const pMap = { 'critical': 4, 'high': 3, 'medium': 2, 'low': 1 };
    if (selectedSort === 'Priority (High → Low)') return (pMap[b.priority?.toLowerCase()] || 0) - (pMap[a.priority?.toLowerCase()] || 0);
    if (selectedSort === 'Priority (Low → High)') return (pMap[a.priority?.toLowerCase()] || 0) - (pMap[b.priority?.toLowerCase()] || 0);
    return 0;
  });

  const totalPages = Math.ceil(sortedTickets.length / rowsPerPage);
  const pagedTickets = sortedTickets.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  const handleUpdateStatus = async (id, status) => {
    try {
      if (status === 'Resolved' || status === 'Closed') {
        navigate(`/tickets/${id}`);
        return;
      }
      await request(`/api/tickets/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      });
      showToast(`Ticket status updated to ${status}`, 'success');
      loadQueue();
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  return (
    <div style={{ padding: '16px 24px', minHeight: 'calc(100vh - 80px)', display: 'flex', flexDirection: 'column' }}>
      <style>{`
        .queue-card {
          background-color: var(--ticket-card); 
          transform: translatex(0px) translatey(0px);
          border-radius: 16px; 
          border: 1px solid ${BORDER}; 
          backdrop-filter: blur(16px); 
          overflow: hidden;
        }
        .queue-table { width: 100%; border-collapse: separate; border-spacing: 0; }
        .queue-th { 
          text-align: left; padding: 12px 16px; 
          background: transparent;
          border-bottom: 1px solid ${BORDER}; 
          color: rgba(156, 163, 175, 0.65); 
          font-size: 11px; font-weight: 700; 
          text-transform: uppercase; letter-spacing: 0.06em; 
        }
        .queue-th:first-child { border-radius: 12px 0 0 0; }
        .queue-th:last-child { border-radius: 0 12px 0 0; }
        .queue-tr { 
          border-bottom: 1px solid ${BORDER}; 
          transition: background 0.15s ease; 
          cursor: pointer; position: relative; 
        }
        .queue-tr:hover { background: rgba(96, 165, 250, 0.07); }
        .queue-td { padding: 14px 16px; font-size: 14px; color: ${PRIMARY_TEXT}; }
        .queue-title { font-weight: 600; color: var(--text-d1d1d1); }
        .priority-badge { font-size: 12px; font-weight: 700; }
        .status-pill { 
          font-size: 11px; font-weight: 700; padding: 3px 10px; 
          border-radius: 6px; text-transform: uppercase; letter-spacing: 0.03em;
          display: inline-block;
        }
        .quick-action-btn { 
          padding: 4px 10px; font-size: 11px; font-weight: 600; 
          border: 1px solid ${BORDER}; 
          background: var(--white-04); 
          border-radius: 6px; cursor: pointer; 
          color: ${SECONDARY_TEXT}; transition: all 0.15s; 
        }
        .quick-action-btn:hover { background: rgba(96, 165, 250,0.15); color: var(--text-60a5fa); border-color: rgba(96, 165, 250,0.25); }
        .queue-search-input { 
          padding: 8px 12px 8px 34px; border-radius: 8px; 
          border: 1px solid ${BORDER}; 
          font-size: 13px; width: 220px; outline: none; 
          background: rgba(0,0,0,0.3); 
          color: var(--text-primary); 
          transition: border-color 0.2s;
        }
        .queue-search-input::placeholder { color: var(--text-muted); }
        .queue-search-input:focus { border-color: rgba(96, 165, 250,0.4); }
        .queue-search-btn {
          height: 36px; padding: 0 16px; 
          background: rgba(96, 165, 250,0.18); color: var(--text-60a5fa); 
          border: 1px solid rgba(96, 165, 250,0.25); border-radius: 8px; 
          font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.2s;
        }
        .queue-search-btn:hover { background: rgba(96, 165, 250,0.28); }
        @keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
        .spin { animation: spin 1s linear infinite; }
        .queue-footer { 
          height: 52px; padding: 0 24px; display: flex; justify-content: space-between; align-items: center; 
          border-top: 1px solid ${BORDER}; background: transparent; flex-shrink: 0;
        }
        .filter-chip { 
          display: inline-flex; align-items: center; padding: 6px 12px; border-radius: 8px; font-size: 12px; font-weight: 600; 
          background: var(--white-03); color: var(--text-muted); border: 1px solid var(--white-05); 
          cursor: pointer; transition: all 0.2s ease; 
        }
        .filter-chip:hover { background: var(--white-05); border-color: rgba(255,255,255,0.2); color: var(--text-primary); }
        .filter-chip:disabled { opacity: 0.4; cursor: not-allowed; }
      `}</style>

      {/* HEADER */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 700, color: PRIMARY_TEXT }}>
            My Queue <span style={{ fontSize: '0.9rem', color: SECONDARY_TEXT, fontWeight: 500 }}>({filteredTickets.length})</span>
          </h1>
          <p style={{ margin: '2px 0 0', fontSize: '13px', color: SECONDARY_TEXT }}>Tickets assigned to you</p>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ position: 'relative' }}>
              <button
                className="filter-chip"
                onClick={() => setShowSort(!showSort)}
                style={{ height: 36, gap: 6 }}
              >
                Sort: {selectedSort}
              </button>
              {showSort && (
                <div style={{ position: 'absolute', top: 44, right: 0, background: 'rgba(13,13,20,0.98)', border: '1px solid var(--white-05)', borderRadius: 10, boxShadow: '0 12px 40px rgba(0,0,0,0.6)', zIndex: 50, minWidth: 200, backdropFilter: 'blur(12px)', padding: 4 }}>
                  {['Latest Activity', 'Oldest Activity', 'Priority (High → Low)', 'Priority (Low → High)'].map(s => (
                    <div
                      key={s}
                      onClick={() => { setSelectedSort(s); setShowSort(false); setCurrentPage(1); }}
                      style={{ padding: '10px 14px', fontSize: 13, cursor: 'pointer', color: selectedSort === s ? 'var(--text-60a5fa)' : 'var(--text-secondary)', background: selectedSort === s ? 'rgba(96, 165, 250,0.12)' : 'transparent', borderRadius: 7 }}
                    >
                      {s}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={{ position: 'relative' }}>
              <Search size={14} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
              <input
                type="text"
                placeholder="Search queue..."
                value={search}
                onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
                onKeyDown={e => e.key === 'Enter' && loadQueue()}
                className="queue-search-input"
              />
            </div>
            <button className="queue-search-btn" onClick={loadQueue}>Search</button>
          </div>
          <button
            onClick={loadQueue}
            style={{ height: 36, width: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.05)', border: `1px solid ${BORDER}`, borderRadius: 8, cursor: 'pointer', color: 'var(--text-secondary)', transition: 'all 0.2s' }}
            title="Refresh"
          >
            <RefreshCw size={16} className={loading ? 'spin' : ''} />
          </button>
        </div>
      </div>

      {/* TICKET LIST */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        {loading && tickets.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div className="spinner" />
          </div>
        ) : filteredTickets.length === 0 ? (
          <div className="queue-card" style={{ textAlign: 'center', padding: '80px 20px' }}>
            <Clock size={48} strokeWidth={1} style={{ color: 'rgba(96, 165, 250, 0.3)', marginBottom: 16 }} />
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: PRIMARY_TEXT }}>Your queue is empty</h3>
            <p style={{ margin: '8px 0 0', color: SECONDARY_TEXT, fontSize: 14 }}>Great job! You're all caught up.</p>
          </div>
        ) : (
          <div className="queue-card" style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            <div style={{ flex: 1, overflowY: 'auto' }}>
              <table className="queue-table">
                <thead>
                  <tr>
                    <th className="queue-th">ID</th>
                    <th className="queue-th">Ticket Title</th>
                    <th className="queue-th">Priority</th>
                    <th className="queue-th">Status</th>
                    <th className="queue-th" style={{ textAlign: 'right' }}>Last Activity</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedTickets.map((t) => {
                    const priorityKey = (t.priority || 'low').toLowerCase();
                    const sStyle = STATUS_STYLES[t.status] || STATUS_STYLES['Open'];
                    return (
                      <tr
                        key={t.id}
                        className="queue-tr"
                        onClick={() => navigate(`/tickets/${t.id}`)}
                        onMouseEnter={() => setHoveredRow(t.id)}
                        onMouseLeave={() => setHoveredRow(null)}
                      >
                        <td className="queue-td" style={{ fontWeight: 700, width: 100, color: 'var(--text-secondary)', fontSize: 12, letterSpacing: '0.02em' }}>BST-INT{t.id}</td>
                        <td className="queue-td">
                          <div className="queue-title">{t.title}</div>
                          <div style={{ fontSize: 11, color: SECONDARY_TEXT, marginTop: 2 }}>{t.category || 'Support'}</div>
                        </td>
                        <td className="queue-td">
                          <div className="priority-badge" style={{ color: PRIORITY_COLORS[priorityKey] || PRIORITY_COLORS.low, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'currentColor' }} />
                            {t.priority}
                          </div>
                        </td>
                        <td className="queue-td">
                          <div className="status-pill" style={{ background: sStyle.bg, color: sStyle.color, border: `1px solid ${sStyle.border}` }}>
                            {t.status}
                          </div>
                        </td>
                        <td className="queue-td" style={{ textAlign: 'right' }}>
                          <div style={{ fontWeight: 600, fontSize: 12 }}>{timeAgo(t.updated_at || t.created_at)}</div>
                          <div style={{ fontSize: 11, color: SECONDARY_TEXT, marginTop: 2 }}>{new Date(t.updated_at || t.created_at).toLocaleDateString()}</div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="queue-footer">
              <div style={{ fontSize: 12, color: SECONDARY_TEXT, fontWeight: 500 }}>
                Showing {Math.min(sortedTickets.length, (currentPage - 1) * rowsPerPage + 1)} to {Math.min(sortedTickets.length, currentPage * rowsPerPage)} of {sortedTickets.length}
              </div>

              <div style={{ display: 'flex', gap: 4 }}>
                <button className="filter-chip" onClick={() => setCurrentPage(1)} disabled={currentPage === 1} style={{ padding: '0 8px', height: 28 }}>{"<<"}</button>
                <button className="filter-chip" onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1} style={{ padding: '0 8px', height: 28 }}>{"<"}</button>
                <div style={{ display: 'flex', alignItems: 'center', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', minWidth: 60, justifyContent: 'center' }}>
                  Page {currentPage} of {totalPages || 1}
                </div>
                <button className="filter-chip" onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages} style={{ padding: '0 8px', height: 28 }}>{">"}</button>
                <button className="filter-chip" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} style={{ padding: '0 8px', height: 28 }}>{">>"}</button>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: SECONDARY_TEXT, fontWeight: 500 }}>
                Rows:
                <select
                  value={rowsPerPage}
                  onChange={e => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                  style={{
                    height: 28,
                    padding: '0 8px',
                    minWidth: 60,
                    border: '1px solid var(--white-05)',
                    borderRadius: 6,
                    background: 'rgba(0,0,0,0.3)',
                    color: 'var(--text-secondary)',
                    fontSize: 12,
                    outline: 'none',
                    cursor: 'pointer'
                  }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
